/**
 * @fileoverview Self-contained state management for EditStudentsModal.
 * No shared context. No Provider. Pure local state + derived values.
 *
 * Single source of truth: allStudents[] + assignedIds Set<string>
 * Everything else (availableStudents, assignedStudents, isDirty) is derived.
 *
 * Uses legacy GET /students/for_project endpoint which returns all group
 * students with a `selected` flag indicating project assignment.
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { api } from 'helpers'

/** Student with display info */
export interface EditStudent {
  id: string
  name: string
  avatar?: string
}

/** Backend response from GET /students/for_project (legacy endpoint) */
interface ForProjectStudent {
  id: string
  name: string
  photo_url: string
  in_group: boolean
  selected: boolean
}

interface UseEditStudentsStateParams {
  projectId: string
  schoolTermId: string
  opened: boolean
}

/** Compares two Sets for equality */
function setsAreEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const item of a) {
    if (!b.has(item)) return false
  }
  return true
}

export function useEditStudentsState({ projectId, schoolTermId, opened }: UseEditStudentsStateParams) {
  // Core state
  const [allStudents, setAllStudents] = useState<EditStudent[]>([])
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set())
  const [initialAssignedIds, setInitialAssignedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!opened || !projectId || !schoolTermId) return

    let cancelled = false

    async function loadData() {
      setIsLoading(true)

      try {
        const { data } = await api.get<ForProjectStudent[]>(
          `/students/for_project?project_id=${projectId}&school_term_id=${schoolTermId}`,
        )
        if (cancelled) return

        const students = Array.isArray(data) ? data : JSON.parse(data as unknown as string)

        const mapped: EditStudent[] = students
          .filter((s: ForProjectStudent) => s.id)
          .map((s: ForProjectStudent) => ({ id: s.id, name: s.name || '', avatar: s.photo_url }))

        const selectedIds = new Set<string>(
          students.filter((s: ForProjectStudent) => s.selected).map((s: ForProjectStudent) => s.id),
        )

        setAllStudents(mapped)
        setAssignedIds(selectedIds)
        setInitialAssignedIds(selectedIds)
      } catch (err) {
        console.error('[EditStudents] Failed to load:', err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadData()
    return () => { cancelled = true }
  }, [opened, projectId, schoolTermId])

  // Derived state (never stored, always computed)
  const assignedStudents = useMemo(
    () => allStudents.filter(s => assignedIds.has(s.id)),
    [allStudents, assignedIds],
  )

  const availableStudents = useMemo(() => {
    const available = allStudents.filter(s => !assignedIds.has(s.id))
    if (!searchQuery.trim()) return available
    const query = searchQuery.toLowerCase()
    return available.filter(s => s.name.toLowerCase().includes(query))
  }, [allStudents, assignedIds, searchQuery])

  const isDirty = useMemo(
    () => !setsAreEqual(assignedIds, initialAssignedIds),
    [assignedIds, initialAssignedIds],
  )

  const addStudent = useCallback((id: string) => {
    setAssignedIds(prev => new Set([...prev, id]))
  }, [])

  const removeStudent = useCallback((id: string) => {
    setAssignedIds(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  const addAllStudents = useCallback(() => {
    setAssignedIds(new Set(allStudents.map(s => s.id)))
  }, [allStudents])

  return {
    // Data
    allStudents,
    assignedStudents,
    availableStudents,
    // UI state
    isLoading,
    isSaving,
    setIsSaving,
    isDirty,
    searchQuery,
    setSearchQuery,
    // Actions
    addStudent,
    removeStudent,
    addAllStudents,
    // For save
    assignedIds,
  }
}
